import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

// ============================================================================
// INTERFACES
// ============================================================================

export interface PurchaseOrderItem {
  id: string;
  series: string;
  purchaseRequest: any | null;
  vendorCode: string;
  vendor?: {
    vendorCode?: string;
    vendorName?: string;
    vendorEmail?: string;
    vendorPhone?: string;
    vendorAddress?: string;
    [key: string]: any;
  };
  description: string;
  deliveryDate: string;
  creationDate: string;
  total: number;
  netTotal: number;
  // Additional fields for compatibility
  supplier: string;
  company: string;
  status: string;
  // Items array for detail view
  items?: any[];
}

export interface PurchaseOrderListResponse {
  data: PurchaseOrderItem[];
  totalRecords: number;
  loading: boolean;
}

// ============================================================================
// STATUS OPTIONS
// ============================================================================

export const PURCHASE_ORDER_STATUS_OPTIONS = [
  { label: 'purchase_order.status.draft', value: 'DRAFT' },
  { label: 'purchase_order.status.pending', value: 'PENDING' },
  { label: 'purchase_order.status.invoiced', value: 'INVOICED' },
  { label: 'purchase_order.status.received', value: 'RECEIVED' },
  { label: 'purchase_order.status.completed', value: 'COMPLETED' },
  { label: 'purchase_order.status.cancelled', value: 'CANCELLED' },
  { label: 'purchase_order.status.closed', value: 'CLOSED' },
  { label: 'purchase_order.status.delivered', value: 'DELIVERED' },
];

// Status options used by list filter (limited to workflow statuses)
export const PURCHASE_ORDER_STATUS_FILTER_OPTIONS = [
  { label: 'purchase_order.status.pending', value: 'PENDING' },
  { label: 'purchase_order.status.approved', value: 'APPROVED' },
  { label: 'purchase_order.status.rejected', value: 'REJECTED' },
  { label: 'purchase_order.status.sent', value: 'SENT' },
  {
    label: 'purchase_order.status.vendor_confirmed',
    value: 'VENDOR_CONFIRMED',
  },
  { label: 'purchase_order.status.delivered', value: 'DELIVERED' },
  { label: 'purchase_order.status.done', value: 'DONE' },
  { label: 'purchase_order.status.closed', value: 'CLOSED' },
  { label: 'purchase_order.status.cancelled', value: 'CANCELLED' },
];

// ============================================================================
// SEARCH FORM CONFIGURATION
// ============================================================================

export const PURCHASE_ORDER_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'series',
    label: 'purchase_order.search.fields.series',
    type: 'text',
    placeholder: 'purchase_order.search.placeholders.enter_series',
  },
  {
    key: 'purchaseRequest',
    label: 'purchase_order.search.fields.purchase_request',
    type: 'number',
    placeholder: 'purchase_order.search.placeholders.enter_purchase_request',
  },
  {
    key: 'vendorCode',
    label: 'purchase_order.search.fields.vendor_code',
    type: 'text',
    placeholder: 'purchase_order.search.placeholders.enter_vendor_code',
  },
  {
    key: 'status',
    label: 'purchase_order.table.columns.status',
    type: 'select',
    options: PURCHASE_ORDER_STATUS_FILTER_OPTIONS,
    showClear: true,
  },
];

// ============================================================================
// TABLE COLUMN CONFIGURATION
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const PURCHASE_ORDER_DEFAULT_COLUMNS = {
  SERIES: 'series',
  PURCHASE_REQUEST: 'purchaseRequest',
  VENDOR_CODE: 'vendorCode',
  DESCRIPTION: 'description',
  STATUS: 'status',
  DELIVERY_DATE: 'deliveryDate',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Action column property name
 */
export const PURCHASE_ORDER_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const PURCHASE_ORDER_DEFAULT_COLUMNS_ORDER: string[] = [
  PURCHASE_ORDER_DEFAULT_COLUMNS.SERIES,
  PURCHASE_ORDER_DEFAULT_COLUMNS.PURCHASE_REQUEST,
  PURCHASE_ORDER_DEFAULT_COLUMNS.VENDOR_CODE,
  PURCHASE_ORDER_DEFAULT_COLUMNS.DESCRIPTION,
  PURCHASE_ORDER_DEFAULT_COLUMNS.STATUS,
  PURCHASE_ORDER_DEFAULT_COLUMNS.DELIVERY_DATE,
  PURCHASE_ORDER_DEFAULT_COLUMNS.CREATION_DATE,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const PURCHASE_ORDER_FLEXIBLE_COLUMNS_ORDER: string[] = [];

export const PURCHASE_ORDER_COLUMNS: TableColumn[] = [
  {
    label: 'purchase_order.table.columns.series',
    prop: PURCHASE_ORDER_DEFAULT_COLUMNS.SERIES,
    width: 120,
    align: 'center',
  },
  {
    label: 'purchase_order.table.columns.purchase_request',
    prop: PURCHASE_ORDER_DEFAULT_COLUMNS.PURCHASE_REQUEST,
    width: 120,
    type: 'custom',
    align: 'center',
  },
  {
    label: 'purchase_order.table.columns.vendor_code',
    prop: PURCHASE_ORDER_DEFAULT_COLUMNS.VENDOR_CODE,
    width: 120,
    align: 'center',
  },
  {
    label: 'purchase_order.table.columns.description',
    prop: PURCHASE_ORDER_DEFAULT_COLUMNS.DESCRIPTION,
    width: 150,
    align: 'center',
  },
  {
    label: 'purchase_order.table.columns.status',
    prop: PURCHASE_ORDER_DEFAULT_COLUMNS.STATUS,
    width: 120,
    type: 'custom',
    align: 'center',
  },
  {
    label: 'purchase_order.table.columns.delivery_date',
    prop: PURCHASE_ORDER_DEFAULT_COLUMNS.DELIVERY_DATE,
    width: 120,
    type: 'custom',
    align: 'center',
  },
  {
    label: 'purchase_order.table.columns.creation_date',
    prop: PURCHASE_ORDER_DEFAULT_COLUMNS.CREATION_DATE,
    width: 120,
    type: 'custom',
    align: 'center',
  },
  {
    label: 'purchase_order.table.columns.actions',
    prop: PURCHASE_ORDER_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 200,
    align: 'center',
  },
];

// ============================================================================
// BUTTON CONFIGURATION
// ============================================================================

export const PURCHASE_ORDER_BUTTON_CONFIG = {
  label: 'common.actions.new',
  severity: 'info' as const,
  show: true,
  icon: 'add',
};

// ============================================================================
// STATUS MAPPING FUNCTIONS
// ============================================================================

export const getStatusSeverity = (
  status: string,
): 'success' | 'warning' | 'danger' | 'secondary' => {
  switch (status) {
    case 'COMPLETED':
    case 'DELIVERED':
    case 'DONE':
    case 'APPROVED':
      return 'success';
    case 'PENDING':
    case 'INVOICED':
    case 'RECEIVED':
    case 'SENT':
    case 'VENDOR_CONFIRMED':
      return 'warning';
    case 'CANCELLED':
    case 'REJECTED':
      return 'danger';
    case 'DRAFT':
    case 'CLOSED':
    default:
      return 'secondary';
  }
};

export const getStatusDisplayText = (
  status: string,
  translateService?: any,
): string => {
  const statusMap: { [key: string]: string } = {
    DRAFT: 'purchase_order.status.draft',
    PENDING: 'purchase_order.status.pending',
    APPROVED: 'purchase_order.status.approved',
    REJECTED: 'purchase_order.status.rejected',
    SENT: 'purchase_order.status.sent',
    VENDOR_CONFIRMED: 'purchase_order.status.vendor_confirmed',
    DELIVERED: 'purchase_order.status.delivered',
    DONE: 'purchase_order.status.done',
    CLOSED: 'purchase_order.status.closed',
    CANCELLED: 'purchase_order.status.cancelled',
    INVOICED: 'purchase_order.status.invoiced',
    RECEIVED: 'purchase_order.status.received',
    COMPLETED: 'purchase_order.status.completed',
  };

  const translationKey = statusMap[status] || 'purchase_order.status.draft';
  return translateService
    ? translateService.instant(translationKey)
    : translationKey;
};

export const getStatusIcon = (status: string): string => {
  switch (status) {
    case 'COMPLETED':
    case 'DELIVERED':
    case 'DONE':
    case 'APPROVED':
      return 'pi pi-check-circle text-green-500';
    case 'PENDING':
    case 'INVOICED':
    case 'RECEIVED':
    case 'SENT':
    case 'VENDOR_CONFIRMED':
      return 'pi pi-clock text-yellow-500';
    case 'CANCELLED':
    case 'REJECTED':
      return 'pi pi-times-circle text-red-500';
    case 'DRAFT':
      return 'pi pi-file text-gray-500';
    case 'CLOSED':
      return 'pi pi-lock text-gray-500';
    default:
      return 'pi pi-file text-gray-500';
  }
};

export const getStatusStyle = (status: string): any => {
  const severity = getStatusSeverity(status);

  switch (severity) {
    case 'success':
      return {
        'background-color': '#ecfdf5',
        color: '#047857',
        border: 'none',
      };
    case 'warning':
      return {
        'background-color': '#fef3c7',
        color: '#a16207',
        border: 'none',
      };
    case 'danger':
      return {
        'background-color': '#fef2f2',
        color: '#dc2626',
        border: 'none',
      };
    case 'secondary':
    default:
      return {
        'background-color': '#f9fafb',
        color: '#6b7280',
        border: 'none',
      };
  }
};

// ============================================================================
// CURRENCY FORMATTING
// ============================================================================

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};
